import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { LogoUploadField } from '@/shared/components/logo-upload'
import { AddressFieldsEditor } from '@/shared/components/address-fields'
import type { DirtyMap } from '@/shared/utils/diffAddressesForUpdate'
import {
  updateBusinessSchema,
  type UpdateBusinessFormValues,
} from '../../schemas/updateBusinessSchema'
import type { BusinessDetail } from '../../hooks/useBusiness'

export const BUSINESS_EDIT_FORM_ID = 'business-edit-form'

interface BusinessEditFormProps {
  business: NonNullable<BusinessDetail>
  onSubmit: (
    values: UpdateBusinessFormValues,
    dirty: DirtyMap<UpdateBusinessFormValues>,
  ) => void | Promise<void>
  disabled?: boolean
}

function deriveDefaults(business: NonNullable<BusinessDetail>): UpdateBusinessFormValues {
  const addresses = (business.addresses ?? []).map((a) => ({
    id: a.id,
    _deleted: false,
    street1: a.street1 ?? '',
    city: a.city ?? '',
    countryId: a.countryId ?? 0,
    provinceId: a.provinceId ?? 0,
    municipalityId: a.municipalityId ?? 0,
    coordinateLatitude: a.coordinateLatitude ?? '',
    coordinateLongitude: a.coordinateLongitude ?? '',
    isMain: a.isMain ?? false,
  }))
  return {
    legalName: business.legalName ?? '',
    logo: business.logo ?? '',
    addresses,
  }
}

/**
 * Form de edición de negocio. NO renderiza botones de submit — el page padre los
 * coloca y los enlaza con `form={BUSINESS_EDIT_FORM_ID}` (mismo patrón que
 * `OrganizationEditForm`). Maneja alta/edición/baja de direcciones.
 *
 * Nota: el backend (`PortalUpdateBusinessInput`) no expone categorías, así que
 * no se editan aquí.
 */
export function BusinessEditForm({ business, onSubmit, disabled }: BusinessEditFormProps) {
  const form = useForm<UpdateBusinessFormValues>({
    resolver: zodResolver(updateBusinessSchema),
    defaultValues: deriveDefaults(business),
    mode: 'onBlur',
  })

  // Solo envía lo modificado: pasa `dirtyFields` al consumidor.
  const submit = form.handleSubmit(async (values) => {
    await onSubmit(values, form.formState.dirtyFields)
  })

  return (
    <FormProvider {...form}>
      <form id={BUSINESS_EDIT_FORM_ID} onSubmit={submit}>
        <fieldset disabled={disabled} className="space-y-6 border-0 p-0 m-0">
          <Card>
            <CardHeader>
              <CardTitle>Datos generales</CardTitle>
            </CardHeader>
            <div className="grid gap-6 sm:grid-cols-[140px_1fr]">
              <FormField
                control={form.control}
                name="logo"
                render={({ field }) => (
                  <FormItem className="flex items-start justify-center sm:justify-start">
                    <LogoUploadField
                      value={field.value ?? null}
                      onChange={(url) => field.onChange(url ?? '')}
                      disabled={disabled}
                    />
                  </FormItem>
                )}
              />
              <div className="grid gap-5">
                <FormField
                  control={form.control}
                  name="legalName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Nombre legal</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </Card>

          <AddressFieldsEditor name="addresses" disabled={disabled} />
        </fieldset>
      </form>
    </FormProvider>
  )
}
